import { of, EMPTY, fromEvent, merge } from 'rxjs';
import { switchMap, catchError, tap, defaultIfEmpty } from 'rxjs/operators';
import { fetchJson, postJson, deleteEntity } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';

/**
 * Renders a list of students into the given <ul> element. Clears the list
 * first, then appends one <li> per student with a name span and a delete button.
 */
function renderStudents(listEl, students) {
    listEl.innerHTML = '';
    for (let student of students) {
        let li = document.createElement('li');
        li.innerHTML = `<span class="student-name">${student.name}</span><button class="delete-student-btn" data-id="${student.id}">Löschen</button>`;
        listEl.appendChild(li);
    }
}

/**
 * Creates and returns the students view Observable. Fetches all students from
 * GET /api/students on mount, renders them, and wires up the add form
 * (button click and Enter key) and per-row delete buttons (event delegation).
 */
export function createStudentsView() {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('students-view');
            replaceViewContainer(el);

            let nameInput = el.querySelector('.student-name-input');
            let addBtn = el.querySelector('.add-student-btn');
            let statusEl = el.querySelector('.status');
            let listEl = el.querySelector('.students-list');

            function fetchAndRender() {
                return fetchJson('/api/students').pipe(
                    tap((students) => renderStudents(listEl, students)),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let load$ = fetchAndRender();

            function submitAdd() {
                let name = nameInput.value.trim();
                if (!name) {
                    statusEl.textContent = 'Bitte einen Namen eingeben.';
                    return EMPTY;
                }
                statusEl.textContent = 'Wird gespeichert…';
                return postJson('/api/students', { name }).pipe(
                    switchMap(() => {
                        nameInput.value = '';
                        statusEl.textContent = 'Schüler hinzugefügt.';
                        return fetchAndRender();
                    }),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let add$ = fromEvent(addBtn, 'click').pipe(
                switchMap(() => submitAdd())
            );

            let enter$ = fromEvent(nameInput, 'keydown').pipe(
                switchMap((e) => (e.key === 'Enter' ? submitAdd() : EMPTY))
            );

            let delete$ = fromEvent(listEl, 'click').pipe(
                switchMap((e) => {
                    let btn = e.target.closest('.delete-student-btn');
                    if (!btn) {
                        return EMPTY;
                    }
                    if (!window.confirm('Schüler wirklich löschen?')) {
                        return EMPTY;
                    }
                    let id = btn.dataset.id;
                    return deleteEntity(`/api/students/${id}`).pipe(
                        defaultIfEmpty(null),
                        switchMap(() => fetchAndRender()),
                        catchError((err) => {
                            showError(err);
                            return EMPTY;
                        })
                    );
                })
            );

            return merge(load$, add$, enter$, delete$);
        })
    );
}
